import { useRef, useState, type MouseEvent } from 'react';
import { documentoDeArchivo } from '../lib/fotos.js';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { useFoto } from './Miniatura.js';

/**
 * La foto de la cédula de un cliente: tomarla, cambiarla, quitarla.
 *
 * Taken at the counter when somebody new is about to be trusted with fiado. The whole card,
 * big enough to read its name and number back (`prepararDocumento`), and like a product photo
 * it is made and kept on this device first, so it works with no signal.
 *
 * One file input with no `capture`, as the product photo had until it opened the camera itself
 * (2026-10-20): for a choice of the camera or the gallery, for the client who already has a
 * picture of their card on WhatsApp.
 */
export function TomarFotoCedula({
  huella,
  onCambio,
  textos,
}: {
  huella: string | null;
  onCambio: (huella: string | null) => void;
  /** The same thing for another document — a receipt, say — in its own words. */
  textos?: { etiqueta: string; ayuda: string; tomar: string; cambiar: string };
}) {
  const t = useT();
  const entrada = useRef<HTMLInputElement>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function alElegir(archivo: File | undefined) {
    if (!archivo) return;
    setTrabajando(true);
    setError(null);
    try {
      onCambio(await documentoDeArchivo(archivo));
    } catch (e) {
      console.warn('[cédula]', e);
      setError(t('foto.noSePudo'));
    } finally {
      setTrabajando(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="etiqueta">{textos?.etiqueta ?? t('clientes.fotoCedula')}</span>
      {huella && <VerFotoCedula huella={huella} {...(textos ? { titulo: textos.etiqueta } : {})} />}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          className="btn-secundario h-11 min-h-0 px-4 text-sm"
          disabled={trabajando}
          onClick={() => entrada.current?.click()}
        >
          <Ico icono="foto" className="size-4" />
          {trabajando
            ? t('foto.preparando')
            : huella
              ? (textos?.cambiar ?? t('clientes.cambiarFotoCedula'))
              : (textos?.tomar ?? t('clientes.tomarFotoCedula'))}
        </button>
        {huella && (
          <button
            type="button"
            className="btn-texto gap-1.5 text-sm text-slate-400"
            disabled={trabajando}
            onClick={() => onCambio(null)}
          >
            <Ico icono="quitar" className="size-4" />
            {t('foto.quitar')}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500">{textos?.ayuda ?? t('clientes.ayudaFotoCedula')}</p>
      {error && <p className="text-xs text-rose-400">{error}</p>}
      <input
        ref={entrada}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void alElegir(e.target.files?.[0])}
      />
    </div>
  );
}

/**
 * The card, as a picture to read: small where it sits, the whole screen when tapped, and closer
 * again on a second tap — the app turns off pinching (`maximum-scale=1`), so the fine print of
 * a cédula needs its own way in. Zoomed, it scrolls in both directions.
 *
 * On a phone that did not take it, the photo is fetched the first time somebody looks — this
 * component asking for it is what brings it down — and until it arrives, that is what it says.
 */
export function VerFotoCedula({ huella, titulo }: { huella: string; titulo?: string }) {
  const t = useT();
  const url = useFoto(huella);
  const [grande, setGrande] = useState(false);
  const [cerca, setCerca] = useState(false);
  const marco = useRef<HTMLDivElement>(null);

  /** Closer on the spot that was tapped, so what somebody wanted to read is what they get. */
  function alTocar(e: MouseEvent<HTMLButtonElement>) {
    if (cerca) {
      setCerca(false);
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    // A key press has no spot: the middle.
    const x = e.detail === 0 ? 0.5 : (e.clientX - r.left) / r.width;
    const y = e.detail === 0 ? 0.5 : (e.clientY - r.top) / r.height;
    setCerca(true);
    requestAnimationFrame(() => {
      const m = marco.current;
      if (!m) return;
      m.scrollLeft = x * m.scrollWidth - m.clientWidth / 2;
      m.scrollTop = y * m.scrollHeight - m.clientHeight / 2;
    });
  }

  if (url === undefined) return <div className="h-32 w-full max-w-xs rounded-xl bg-slate-800/60" />;
  if (url === null) return <p className="text-xs text-slate-500">{t('cuenta.fotoNoLlega')}</p>;
  return (
    <>
      <button
        type="button"
        className="block w-full max-w-xs overflow-hidden rounded-xl bg-slate-950 ring-1 ring-white/10 transition cursor-zoom-in hover:ring-white/30"
        onClick={() => setGrande(true)}
        aria-label={t('cuenta.verFotoCedula')}
        data-sin-icono="fila"
      >
        <img src={url} alt="" className="max-h-48 w-full object-contain" />
      </button>
      {grande && (
        <Modal
          titulo={titulo ?? t('clientes.fotoCedula')}
          onCerrar={() => {
            setGrande(false);
            setCerca(false);
          }}
        >
          <div ref={marco} className="max-h-[65dvh] overflow-auto rounded-xl bg-slate-950">
            <button
              type="button"
              className={`block ${cerca ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
              onClick={alTocar}
              aria-label={t(cerca ? 'cuenta.alejarFoto' : 'cuenta.acercarFoto')}
              data-sin-icono="fila"
            >
              <img
                src={url}
                alt={titulo ?? t('clientes.fotoCedula')}
                className={cerca ? 'w-[250%] max-w-none' : 'w-full object-contain'}
              />
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">{t(cerca ? 'cuenta.tocarParaAlejar' : 'cuenta.tocarParaAcercar')}</p>
        </Modal>
      )}
    </>
  );
}
